import { useMemo } from "react";
import { Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";

import { apiFetchJson } from "@/utils/apiFetch";
import InfoTooltip from "@/components/UI/InfoTooltip";

type DocRow = {
  document_id: string;
  title: string | null;
  document_type: string | null;
  category: string | null;
  binder: string | null;
  folder_path: string | null;
  workspace: string | null;
  mrm_status: string | null;
  open_reviews: number | null;
  due_date: string | null;
  expiration_date: string | null;
  uploaded_date: string | null;
};

type Envelope = { count: number; results: DocRow[] };

/**
 * A trial's documents = its study-linked binders plus its ISF workspace's
 * corpus (server-side scope in the `documents` dataset). This tab is
 * exceptions-first: KPI tiles, then only the rows needing action — the full
 * corpus lives inside the trial (Enter trial → Documents).
 */
async function fetchTrialDocuments(trialId: string): Promise<Envelope> {
  return apiFetchJson<Envelope>(
    `${import.meta.env.VITE_API_URL}/data/trial/${encodeURIComponent(trialId)}` +
      `/filters/data/?dataset_type=documents&pagination=${encodeURIComponent(
        JSON.stringify({ page: 1, page_size: 2000 }),
      )}`,
  );
}

const EXPIRY_WINDOW_DAYS = 90;

type Props = { trialId: string; buildUrl: (path: string) => string };

export default function TrialDocumentsTab({ trialId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["trial-documents-tab", trialId],
    queryFn: () => fetchTrialDocuments(trialId),
    staleTime: 60_000,
  });

  const now = Date.now();
  const horizon = now + EXPIRY_WINDOW_DAYS * 24 * 3600 * 1000;

  const { kpis, needsAttention } = useMemo(() => {
    const rows = data?.results ?? [];
    const expected = data?.count ?? rows.length;
    const collected = rows.filter((r) => r.document_type === "content").length;
    const openReviews = rows.reduce((n, r) => n + (r.open_reviews ?? 0), 0);
    const overdue = rows.filter(
      (r) =>
        r.document_type === "placeholder" &&
        r.due_date &&
        Date.parse(r.due_date) < now,
    );
    const expiring = rows.filter(
      (r) =>
        r.expiration_date &&
        Date.parse(r.expiration_date) > now &&
        Date.parse(r.expiration_date) < horizon,
    );
    const withOpenReviews = rows.filter((r) => (r.open_reviews ?? 0) > 0);
    const attention = [
      ...overdue.map((r) => ({ ...r, reason: "Placeholder past due" })),
      ...expiring.map((r) => ({ ...r, reason: "Expiring soon" })),
      ...withOpenReviews.map((r) => ({ ...r, reason: "Open monitor query" })),
    ];
    return {
      kpis: {
        expected,
        collected,
        completeness: expected
          ? `${((100 * collected) / expected).toFixed(1)}%`
          : "—",
        openReviews,
        needingAction: attention.length,
      },
      needsAttention: attention.slice(0, 50),
    };
  }, [data, now, horizon]);

  const columns: ColumnsType<DocRow & { reason: string }> = [
    {
      title: "Reason",
      dataIndex: "reason",
      width: 170,
      render: (v: string) => (
        <Tag color={v === "Open monitor query" ? "orange" : "red"}>{v}</Tag>
      ),
    },
    { title: "Title", dataIndex: "title", ellipsis: true },
    { title: "Type", dataIndex: "document_type", width: 110 },
    { title: "Binder", dataIndex: "binder", ellipsis: true },
    { title: "Folder", dataIndex: "folder_path", ellipsis: true },
    {
      title: "MRM status",
      dataIndex: "mrm_status",
      width: 200,
      ellipsis: true,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
        Loading…
      </div>
    );
  }

  const tiles = [
    { label: "Expected documents", value: kpis.expected },
    { label: "Collected documents", value: kpis.collected },
    { label: "Completeness", value: kpis.completeness },
    { label: "Open monitor queries", value: kpis.openReviews },
    { label: "Needing action", value: kpis.needingAction },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <span className="text-[14px] font-semibold text-[#1F2933]">
          TMF documents
        </span>
        <InfoTooltip title="The trial's study-linked binders plus its ISF workspace corpus. Exceptions shown here; the full document list is inside the trial (Enter trial → Documents)." />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="px-4 py-3 flex flex-col gap-1"
            style={{
              borderRadius: 8,
              border: "1px solid rgba(0, 0, 0, 0.04)",
              background: "#FFF",
              boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
            }}
          >
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#556C75]">
              {t.label}
            </span>
            <span className="text-[22px] font-semibold text-[#1F2933]">
              {t.value}
            </span>
          </div>
        ))}
      </div>
      <div>
        <span className="text-[13px] font-semibold text-[#1F2933]">
          Needs attention
        </span>
        <Table
          className="mt-2"
          size="small"
          rowKey={(r) => `${r.document_id}-${r.reason}`}
          columns={columns}
          dataSource={needsAttention}
          locale={{
            emptyText:
              "Nothing needs attention — no overdue, expiring, or queried documents.",
          }}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      </div>
    </div>
  );
}
